'use client';

import { useLayoutEffect, useEffect, useRef } from 'react';
import { useSession } from 'next-auth/react';
import type User from '@/types/userType';
import type Organization from '@/types/organizationType';
import useUserStore from '@/state/userState';
import useOrganizationStore from '@/state/orgState';
import useLoadingStore from '@/state/loadingState';
import { useUpdatedData } from '@/hooks/useUpdatedData';

interface DashboardBootstrapProps {
  user: User | null;
  organization: Organization | null;
}

/**
 * Seed the stores with what the server already resolved.
 *
 * Dashboard routes gate their whole render on `isInitalFetchDone`, which the
 * client bootstrap only flips after fetching the user and then the org, two
 * serial round trips that every gated page waits through before it may issue
 * its own first request. The layout resolves both server side, next to Django,
 * so this hands them over and releases the gate on the first paint instead.
 *
 * The repository lists are no longer part of this bootstrap. Every consumer
 * that used to read them from the eagerly-populated
 * `organizationRepositoryList` store now fetches what it needs itself, scoped
 * to the route that actually uses it (`useOnboardingChecklist` and
 * `useRepoDocs` on `/home` and `/documentation/*`; `AuthProvider`'s private-repo
 * detection stays lazy and re-fires whenever some other route populates the
 * lists). So when the server handed over a complete identity, this effect has
 * nothing left to do: the stores are already seeded by the layout effect
 * above. An incomplete identity still takes the full `updateData()` path,
 * which carries the `/onboarding` redirect via `validateUserOnboardind`.
 *
 * The latch is per-mount, not global: `useUpdatedData`'s `bootstrapInFlight`
 * is nulled in its own `.finally()`, so it collapses concurrent callers and
 * nothing else. Whatever runs here runs again on the next mount.
 *
 * Seeding runs in a layout effect rather than during render or in a plain
 * effect. Writing to the stores while rendering makes their subscribers
 * (ValidateSession, the gated page) set state mid-render, which React rejects
 * outright. A layout effect commits before the browser paints.
 *
 * This component renders no children. It sits beside the route in the layout so
 * the identity request feeding it streams independently, which is why it can
 * arrive after the page has already mounted; the client bootstrap covers that
 * window, exactly as it did before any of this existed.
 */
// useLayoutEffect has no server counterpart, and this component is server
// rendered before it hydrates.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export default function DashboardBootstrap({
  user,
  organization,
}: DashboardBootstrapProps) {
  const seeded = useRef(false);
  const bootstrapStarted = useRef(false);
  const { status } = useSession();
  const { updateData } = useUpdatedData();

  useIsomorphicLayoutEffect(() => {
    if (seeded.current) return;
    seeded.current = true;

    if (user?.UserUUID) {
      useUserStore.getState().setPureUser(user);
    }

    // Only seed an org the client has not resolved for itself. The client
    // bootstrap prefers `localStorage.OrgName` (useUpdatedData), which the
    // server cannot see, so overwriting a value it already produced would
    // replace the more informed answer with the less informed one. Whichever
    // request lands last would otherwise win, which is not a decision either
    // side should make by accident.
    if (
      organization?.OrgUUID &&
      !useOrganizationStore.getState().organization?.OrgUUID
    ) {
      useOrganizationStore.getState().setOrganization(organization);
    }

    // Only release the gate when the server produced a complete identity. A
    // user with no org has not finished onboarding, and the redirect for that
    // case belongs to the client bootstrap, which needs the gate closed to run
    // it.
    if (user?.UserUUID && organization?.OrgUUID) {
      useLoadingStore.getState().setIsInitialFetchDone(true);
      useLoadingStore.getState().setIsInitialRendered(true);
    }
    // Seeds once from the server payload for this page load; the client
    // bootstrap owns every update after that.
  }, []);

  useEffect(() => {
    if (bootstrapStarted.current) return;
    if (status === 'loading') return;
    bootstrapStarted.current = true;

    // Both halves of the identity arrived as props, so re-fetching them is the
    // one thing this must not do. `updateData` opens with
    // `updateUserAndOrganizationData()`, which spent a `fetchUser/` and a
    // `fetchOrganization/` rediscovering values seeded into the stores a
    // moment earlier. The repository lists used to be the one other thing
    // missing, but no consumer reads them from this bootstrap any more (see
    // the docstring above), so a complete identity now has nothing left to do.
    //
    // The condition matches the gate release above on purpose. A user with a
    // row but no org has not finished onboarding, and the redirect for that
    // case lives in `updateData`'s `validateUserOnboardind`, so an incomplete
    // identity must still take the full path.
    if (user?.UserUUID && organization?.OrgUUID) {
      return;
    }

    void updateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the callback is redefined every render (not memoized); including it would refire this effect on every render, and the props are read as their mount-time values on purpose
  }, [status]);

  // Renders nothing. It is a sibling of the route, not a wrapper around it, so
  // the identity fetch behind it cannot hold up the rest of the layout.
  return null;
}
